import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  fetchConversationStarters,
  fetchConversationStarterPrompts,
  upsertConversationStarter,
} from '../endpoints/api';
import useAuth from './useAuth';

export interface ConversationStarterPrompt {
  id: number;
  text: string;
  sort_order: number;
}

export interface ConversationStarterAnswer {
  prompt_id: number;
  answer: string;
  prompt: {
    id: number;
    text: string;
  };
  updated_at?: string;
}

export const useConversationStarterPrompts = () => {
  return useQuery<ConversationStarterPrompt[]>({
    queryKey: ['conversationStarterPrompts'],
    queryFn: async () => {
      const res = await fetchConversationStarterPrompts();
      return (res as any)?.data?.data || (res as any)?.data || [];
    },
    staleTime: 5 * 60_000, // prompts rarely change
  });
};

export const useConversationStarters = () => {
  const {user} = useAuth();
  const userId = user?.id;

  return useQuery<ConversationStarterAnswer[]>({
    queryKey: ['conversationStarters', userId],
    queryFn: async () => {
      if (!userId) return [];
      const res = await fetchConversationStarters(userId);
      return (res as any)?.data?.data || (res as any)?.data || [];
    },
    enabled: !!userId,
  });
};

export const useUpsertConversationStarter = () => {
  const {user} = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['conversationStarters', user?.id];

  return useMutation({
    mutationFn: (data: {prompt_id: number; answer: string}) => {
      if (!user?.id) throw new Error('Not authenticated');
      return upsertConversationStarter(user.id, data);
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries(queryKey);
      const previous = queryClient.getQueryData<ConversationStarterAnswer[]>(queryKey);
      const prompts =
        queryClient.getQueryData<ConversationStarterPrompt[]>(['conversationStarterPrompts']) || [];
      const promptText = prompts.find((p) => p.id === data.prompt_id)?.text || '';

      queryClient.setQueryData<ConversationStarterAnswer[]>(queryKey, (old) => {
        const list = old ? [...old] : [];
        const idx = list.findIndex((a) => a.prompt_id === data.prompt_id);
        const existing = idx >= 0 ? list[idx] : undefined;
        const next: ConversationStarterAnswer = {
          prompt_id: data.prompt_id,
          answer: data.answer,
          prompt: existing?.prompt ?? {id: data.prompt_id, text: promptText},
          updated_at: existing?.updated_at,
        };
        if (data.answer.length === 0) {
          // Treat empty answer as removal
          if (idx >= 0) list.splice(idx, 1);
        } else if (idx >= 0) {
          list[idx] = next;
        } else {
          list.push(next);
        }
        return list;
      });

      return {previous};
    },
    onError: (_err, _data, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });
};
